import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
	useUpdateRmProfile,
	useUploadRmLogo,
} from "@/api/reseller-manager/mutations";
import { useRmMyProfile } from "@/api/reseller-manager/queries";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/contexts/auth-context";
import { CompanyAppearanceSection } from "./company-appearance-section";
import { CompanyEmailSection } from "./company-email-section";
import { EMPTY_COMPANY_FORM } from "./company-form-types";

export function CompanyTab() {
	const { t } = useTranslation();
	const { updateUser } = useAuth();
	const { data, isLoading } = useRmMyProfile();
	const uploadLogo = useUploadRmLogo();
	const updateProfile = useUpdateRmProfile();
	const logoInputRef = useRef<HTMLInputElement>(null);

	const [form, setForm] = useState(EMPTY_COMPANY_FORM);
	const [initialForm, setInitialForm] = useState(EMPTY_COMPANY_FORM);
	const prevDataRef = useRef<typeof data>(undefined);

	if (data && data !== prevDataRef.current) {
		prevDataRef.current = data;
		const loaded = {
			company_name: data.company_name ?? "",
			primary_color: data.primary_color || "#000000",
			secondary_color: data.secondary_color || "#000000",
			hide_electricity: data.hide_electricity ?? false,
			email_host: data.email_host ?? "",
			email_port: data.email_port ?? 25,
			email_username: data.email_username ?? "",
			email_password: data.email_password ?? "",
			email_from_email: data.email_from_email ?? "",
		};
		setForm(loaded);
		setInitialForm(loaded);
	}

	const isDirty = JSON.stringify(form) !== JSON.stringify(initialForm);

	function handleLogoChange(e: React.ChangeEvent<HTMLInputElement>) {
		const file = e.target.files?.[0];
		if (!file) return;
		const MAX_IMAGE_SIZE = 2 * 1024 * 1024;
		if (file.size > MAX_IMAGE_SIZE) {
			toast.error(t("settings.company.logoError"));
			e.target.value = "";
			return;
		}
		uploadLogo.mutate(
			{ logo: file },
			{
				onSuccess: () => toast.success(t("settings.company.logoUpdated")),
				onError: () => toast.error(t("settings.company.logoError")),
			},
		);
	}

	function handleSave() {
		updateProfile.mutate(form, {
			onSuccess: () => {
				toast.success(t("settings.company.saved"));
				updateUser({
					primary_color: form.primary_color,
					secondary_color: form.secondary_color,
				});
			},
			onError: () => toast.error(t("settings.profile.saveError")),
		});
	}

	if (isLoading) {
		return (
			<Card>
				<CardContent className="flex flex-col gap-4 pt-6">
					<Skeleton className="h-6 w-40" />
					<Skeleton className="h-10 w-full" />
					<Skeleton className="h-6 w-40" />
					<Skeleton className="h-24 w-full" />
				</CardContent>
			</Card>
		);
	}

	return (
		<Card>
			<CardContent className="flex flex-col gap-6 pt-6">
				<div className="flex flex-col gap-3">
					<p className="text-sm font-medium text-muted-foreground">
						{t("settings.company.title")}
					</p>
					<div className="grid gap-2">
						<Label htmlFor="company">{t("settings.company.companyName")}</Label>
						<Input
							id="company"
							autoComplete="organization"
							name="company"
							value={form.company_name}
							onChange={(e) =>
								setForm((f) => ({ ...f, company_name: e.target.value }))
							}
						/>
					</div>
				</div>

				<Separator />

				<CompanyAppearanceSection
					form={form}
					setForm={setForm}
					logoUrl={data?.logo}
					logoInputRef={logoInputRef}
					isUploading={uploadLogo.isPending}
					onLogoChange={handleLogoChange}
				/>

				<Separator />

				<CompanyEmailSection form={form} setForm={setForm} />

				<div className="flex justify-end gap-2">
					<Button
						size="sm"
						variant="outline"
						onClick={() => setForm(initialForm)}
						disabled={!isDirty || updateProfile.isPending}
					>
						{t("settings.profile.cancel")}
					</Button>
					<Button
						size="sm"
						onClick={handleSave}
						disabled={updateProfile.isPending || !isDirty}
					>
						{updateProfile.isPending
							? t("settings.company.saving")
							: t("settings.company.save")}
					</Button>
				</div>
			</CardContent>
		</Card>
	);
}
